import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Save, ArrowLeft, Image, FileText } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import ContentCard from '../../components/admin/ContentCard';
import LoadingState from '../../components/admin/LoadingState';
import RichTextEditor from '../../components/admin/RichTextEditor';
import toast from 'react-hot-toast';
import { createSlug } from '../../utils/slugify';

const BlogForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    category: '',
    author: {
      name: '',
      avatar: ''
    },
    read_time: 5
  });

  useEffect(() => {
    document.title = isEditing ? 'Modifier un Article | Admin' : 'Ajouter un Article | Admin';
    
    if (isEditing) {
      loadPost();
    }
  }, [isEditing, id]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (data) {
        setPost(data);
      }
    } catch (error) {
      console.error('Error loading blog post:', error);
      toast.error('Erreur lors du chargement de l\'article');
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'read_time') {
      setPost({ ...post, [name]: parseInt(value) || 5 });
    } else if (name === 'author_name' || name === 'author_avatar') {
      // Handle nested author object
      const field = name === 'author_name' ? 'name' : 'avatar';
      setPost({
        ...post,
        author: {
          ...post.author,
          [field]: value
        }
      });
    } else {
      setPost({ ...post, [name]: value });
    }
    
    // Auto-generate slug from title
    if (name === 'title' && !isEditing) {
      setPost(prev => ({ ...prev, slug: createSlug(value) }));
    }
  };

  const handleContentChange = (value: string) => {
    setPost({ ...post, content: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Validate required fields
      if (!post.title || !post.slug || !post.excerpt || !post.content || !post.cover_image || !post.category) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      
      // Validate author
      if (!post.author.name) {
        toast.error('Veuillez renseigner le nom de l\'auteur');
        return;
      }
      
      let response;
      
      if (isEditing) {
        // Update existing post
        response = await supabase
          .from('blog_posts')
          .update({
            title: post.title,
            slug: post.slug,
            excerpt: post.excerpt,
            content: post.content,
            cover_image: post.cover_image,
            category: post.category,
            author: post.author,
            read_time: post.read_time
          })
          .eq('id', id);
      } else {
        // Insert new post
        response = await supabase
          .from('blog_posts')
          .insert([{
            title: post.title,
            slug: post.slug,
            excerpt: post.excerpt,
            content: post.content,
            cover_image: post.cover_image,
            category: post.category,
            author: post.author,
            read_time: post.read_time
          }]);
      }
      
      if (response.error) throw response.error;
      
      toast.success(isEditing ? 'Article mis à jour avec succès' : 'Article ajouté avec succès');
      navigate('/admin/blog');
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast.error('Erreur lors de l\'enregistrement de l\'article');
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    'Innovation',
    'Technologie',
    'Environnement',
    'Mobilité',
    'Impact Social',
    'Développement Durable',
    'Économie',
    'Prospective'
  ];

  if (loading) {
    return (
      <div className="px-6">
        <PageHeader
          title={isEditing ? 'Modifier l\'Article' : 'Ajouter un Article'}
          backLink="/admin/blog"
        />
        <LoadingState type="form" />
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title={isEditing ? 'Modifier l\'Article' : 'Ajouter un Article'}
        backLink="/admin/blog"
      />
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <ContentCard title="Contenu de l'Article">
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Titre *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={post.title}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                    Slug URL *
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      /blog/
                    </span>
                    <input
                      type="text"
                      id="slug"
                      name="slug"
                      value={post.slug}
                      onChange={handleChange}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    L'URL unique de cet article (générée automatiquement à partir du titre)
                  </p>
                </div>
                
                <div>
                  <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                    Extrait *
                  </label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={post.excerpt}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Un court résumé de l'article qui apparaîtra dans les listes d'articles
                  </p>
                </div>
                
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                    Contenu *
                  </label>
                  <RichTextEditor
                    value={post.content}
                    onChange={handleContentChange}
                  />
                </div>
              </div>
            </ContentCard>
          </div>
          
          <div>
            <ContentCard title="Métadonnées">
              <div className="space-y-6">
                <div>
                  <label htmlFor="cover_image" className="block text-sm font-medium text-gray-700 mb-1">
                    Image de couverture *
                  </label>
                  <input
                    type="url"
                    id="cover_image"
                    name="cover_image"
                    value={post.cover_image}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://example.com/image.jpg"
                    required
                  />
                  
                  {post.cover_image && (
                    <div className="mt-2 relative">
                      <img
                        src={post.cover_image}
                        alt="Cover"
                        className="w-full h-32 object-cover rounded-md"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x150?text=Image+non+disponible';
                        }}
                      />
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={post.category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="read_time" className="block text-sm font-medium text-gray-700 mb-1">
                    Temps de lecture (minutes)
                  </label>
                  <input
                    type="number"
                    id="read_time"
                    name="read_time"
                    value={post.read_time}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    min="1"
                  />
                </div>
                
                <div>
                  <label htmlFor="author_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de l'auteur *
                  </label>
                  <input
                    type="text"
                    id="author_name"
                    name="author_name"
                    value={post.author.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="author_avatar" className="block text-sm font-medium text-gray-700 mb-1">
                    Avatar de l'auteur (URL)
                  </label>
                  <input
                    type="url"
                    id="author_avatar"
                    name="author_avatar"
                    value={post.author.avatar}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://example.com/avatar.jpg"
                  />
                  
                  {post.author.avatar && (
                    <div className="mt-2 flex justify-center">
                      <img
                        src={post.author.avatar}
                        alt="Author"
                        className="w-16 h-16 object-cover rounded-full"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64?text=Avatar';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/blog')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                {isEditing ? 'Mettre à jour' : 'Publier'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;